import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Check } from 'lucide-react';
import { PRODUCTS } from '../data/mock';

const ProductsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const allProducts = [...PRODUCTS.coreBooths, ...PRODUCTS.premiumExperiences];
  const filteredProducts = activeCategory === 'all'
    ? allProducts
    : activeCategory === 'core'
    ? PRODUCTS.coreBooths
    : PRODUCTS.premiumExperiences;

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Our Products
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-8">
              Premium Photo Booth{' '}
              <span className="gold-gradient-text">Experiences</span>
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
              From classic photo booths to Hollywood-style experiences, we offer a complete range 
              of photo solutions to make your event unforgettable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 border-b border-gray-100 sticky top-20 lg:top-24 z-30 bg-white/95 backdrop-blur-lg">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4 justify-center">
            {[
              { id: 'all', label: 'All Products' },
              { id: 'core', label: 'Core Photo Booths' },
              { id: 'premium', label: 'Premium Experiences' }
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#00BFFF] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="product-card h-full flex flex-col">
                  <div className="product-card-image h-64 lg:h-72">
                    <img src={product.image} alt={product.name} loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-[#00BFFF] text-white text-xs font-semibold uppercase">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <h3 className="font-display text-2xl font-bold text-gray-900 mb-4">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {product.description}
                    </p>
                    
                    {product.highlights && (
                      <div className="mb-6">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 font-medium">Key Highlights</p>
                        <ul className="space-y-2">
                          {product.highlights.map((highlight, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Check className="w-4 h-4 text-[#00BFFF] mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mb-6 mt-auto">
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Best For</p>
                      <div className="flex flex-wrap gap-2">
                        {product.useCases.map((useCase, i) => (
                          <span
                            key={i}
                            className={`service-tag ${
                              useCase.includes('Corporate') || useCase.includes('Trade') || useCase.includes('Conferences') || useCase.includes('Team') ? 'corporate' :
                              useCase.includes('Wedding') || useCase.includes('Parties') || useCase.includes('Celebrations') || useCase.includes('Luxury') || useCase.includes('Themed') ? 'wedding' : 'activation'
                            }`}
                          >
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-gray-100 text-gray-900 hover:bg-[#00BFFF] hover:text-white transition-all text-sm font-medium"
                    >
                      Enquire Now <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              What&apos;s Included
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              Every Booth Comes With
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Professional operators & setup",
              "Unlimited instant prints",
              "Digital gallery & sharing",
              "Custom branding options",
              "Props & accessories",
              "Social media integration",
              "AI-powered filters",
              "Real-time photo delivery",
              "Technical support"
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-100"
              >
                <div className="w-8 h-8 rounded-full bg-[#00BFFF]/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#00BFFF]" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Can&apos;t Decide? We&apos;ll Help!
            </h2>
            <p className="text-gray-600 mb-8">
              Our team will recommend the perfect photo booth experience based on your 
              event type, guest count, and budget.
            </p>
            <Link to="/contact" className="btn-primary">
              Get a Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
